import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Phone, Mail, MapPin, ExternalLink } from 'lucide-react';

export const ContactPage = () => {
    const contactInfo = [
        {
            icon: Phone,
            title: 'Call Us',
            value: '+1 502-289-5604',
            href: 'tel:+15022895604',
            description: 'Mon-Fri 9AM-6PM EST',
        },
        {
            icon: Mail,
            title: 'Email Us',
            value: 'info@bionexxllc.com',
            href: 'mailto:info@bionexxllc.com',
            description: 'We respond within 24 hours',
        },
        {
            icon: MapPin,
            title: 'Visit Us',
            value: '271 W. Short St Ste 410 #2627',
            subvalue: 'Lexington, Kentucky 40507, USA',
            href: 'https://maps.google.com/?q=271+W+Short+St+Lexington+KY+40507',
            description: 'By appointment only',
        },
    ];

    return (
        <main className="pt-20" data-testid="contact-page">
            {/* Hero Section */}
            <section className="py-16 lg:py-24 bg-[#3C3B6E]" data-testid="contact-hero">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-[#B22234] font-medium text-sm uppercase tracking-[0.2em] mb-3">
                        Get in Touch
                    </p>
                    <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight leading-tight mb-6">
                        Contact Us
                    </h1>
                    <p className="text-lg text-white/80 max-w-2xl mx-auto">
                        Have a question or want to work together? Reach out to us via phone or email. We&apos;d love to hear from you!
                    </p>
                </div>
            </section>

            {/* Contact Options */}
            <section className="py-16 lg:py-24 bg-white" data-testid="contact-content">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {contactInfo.map((item, index) => (
                            <Card 
                                key={index}
                                className="bg-gray-50 border border-gray-200 hover:border-[#B22234] transition-all hover:-translate-y-2"
                                data-testid={`contact-card-${item.title.toLowerCase().replace(' ', '-')}`}
                            >
                                <CardContent className="p-8 text-center">
                                    <div className="w-16 h-16 flex items-center justify-center bg-[#3C3B6E] rounded-full mx-auto mb-6">
                                        <item.icon className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-[#3C3B6E] mb-2">
                                        {item.title}
                                    </h3>
                                    <a 
                                        href={item.href}
                                        target={item.title === 'Visit Us' ? '_blank' : undefined}
                                        rel={item.title === 'Visit Us' ? 'noopener noreferrer' : undefined}
                                        className="text-[#B22234] font-semibold hover:underline block mb-1"
                                    >
                                        {item.value}
                                        {item.title === 'Visit Us' && (
                                            <ExternalLink className="w-4 h-4 inline ml-1" />
                                        )}
                                    </a>
                                    {item.subvalue && (
                                        <p className="text-gray-600 text-sm mb-2">{item.subvalue}</p>
                                    )}
                                    <p className="text-gray-500 text-sm mt-2">{item.description}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    {/* Direct Contact Buttons */}
                    <div className="mt-12 text-center">
                        <p className="text-gray-600 mb-6">Ready to get started? Contact us directly:</p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href="mailto:info@bionexxllc.com">
                                <Button 
                                    size="lg"
                                    className="bg-[#B22234] hover:bg-[#8B1A1A] text-white rounded-md px-8 py-6 text-base font-semibold w-full sm:w-auto"
                                    data-testid="email-button"
                                >
                                    <Mail className="mr-2 w-5 h-5" />
                                    Email Us
                                </Button>
                            </a>
                            <a href="tel:+15022895604">
                                <Button 
                                    size="lg"
                                    variant="outline"
                                    className="border-2 border-[#3C3B6E] text-[#3C3B6E] hover:bg-[#3C3B6E] hover:text-white rounded-md px-8 py-6 text-base font-semibold w-full sm:w-auto"
                                    data-testid="call-button"
                                >
                                    <Phone className="mr-2 w-5 h-5" />
                                    Call Us
                                </Button>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Map Section */}
            <section className="py-16 lg:py-24 bg-gray-50" data-testid="map-section">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8">
                        <p className="text-[#B22234] font-medium text-sm uppercase tracking-[0.2em] mb-3">
                            Our Location
                        </p>
                        <h2 className="text-2xl sm:text-3xl font-black text-[#3C3B6E] tracking-tight">
                            Find Us in Lexington, Kentucky
                        </h2>
                    </div>
                    <div className="rounded-lg overflow-hidden shadow-lg border border-gray-200" data-testid="contact-map">
                        <iframe
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3142.274844321289!2d-84.4988478!3d38.0464627!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x884244eb1c42ef89%3A0xc660d249f0ec0f1!2s271%20W%20Short%20St%2C%20Lexington%2C%20KY%2040507!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                            width="100%"
                            height="400"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="BIONEXX LLC Location"
                        ></iframe>
                    </div>
                </div>
            </section>
        </main>
    );
};
